import { Button } from 'components/UI/Elements';
import React, { useEffect, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { quizAnswersState, quizResultsState } from 'utils/recoil/quiz';
import { Component } from 'components/Contentful/component';
import { Label } from 'components/UI/Elements/Label/label';
import { formatContentfulComponentProps } from 'utils/globals';
import { Grid } from 'components/UI/Grid/grid';
import { BlackFridayHero, Hero } from 'components/Marketing/Banners';

const CATEGORY_SCREEN_MAPPING: Record<string, string> = {
  bluelight: 'Product - Bluelight',
  reading: 'Product - Reading',
  sun: 'Product - Sun',
};

const shopItemToId = (shopItem) =>
  [shopItem.product?.shopifyKey, shopItem?.defaultProductVariant?.size?.value]
    .filter((f) => f)
    .join(' - ');

const QuizResult = ({
  hero,
  resetQuiz,
  grid,
  emptyStateLabel,
  banner,
  gaTag: resultTag,
}) => {
  const answers: any = useRecoilValue(quizAnswersState);
  const results = useRecoilValue(quizResultsState);

  const gridItems = useMemo(() => {
    // GET UNIQUE
    let shopItems = [...results].reduce((acc, value) => {
      const uuid = shopItemToId(value);
      if (!acc.find((e) => (e.uuid || shopItemToId(e)) === uuid)) {
        const si = JSON.parse(JSON.stringify(value));
        if (value.defaultProductVariant.size) {
          try {
            si.product.variants = si.product.variants.filter(
              (v) => v.size.value === si.defaultProductVariant.size.value
            );
          } catch (e) {
            console.error(e.message, si);
          }
        }
        acc.push({ uuid, ...si });
      }
      return acc;
    }, []);

    // SHAPE
    {
      if (answers?.form) {
        const forms = answers?.form?.split(',');
        shopItems = shopItems.filter((r) => forms.includes(r.product.shape));
      }
    }

    // SUB CATEGORY filter (if kids or senior with correction)
    {
      if (['kid', 'senior'].includes(answers.age)) {
        shopItems = shopItems.filter((r) => {
          // kid screen type
          if (answers.age === 'kid') {
            return r.product.categories.find(
              (c) => c.title === CATEGORY_SCREEN_MAPPING[answers.screen]
            );
          } else if (answers.screen === 'reading') {
            if (answers.screenTime !== 'never') {
              return r.product.categories.find(
                (c) => c.title === CATEGORY_SCREEN_MAPPING.bluelight
              );
            } else {
              return !r.product.categories.find(
                (c) =>
                  c.title === CATEGORY_SCREEN_MAPPING.bluelight ||
                  c.title === CATEGORY_SCREEN_MAPPING.sun
              );
            }
          } else if (answers.screen !== 'reading') {
            if (answers.needCorrection === 'yes') {
              return r.product.categories.find(
                (c) => c.title === CATEGORY_SCREEN_MAPPING.reading
              );
            } else {
              return !r.product.categories.find(
                (c) => c.title === CATEGORY_SCREEN_MAPPING.reading
              );
            }
          }

          return true;
        });
      }
    }

    // COLOR filter
    {
      if (answers.color) {
        const colors = answers.color?.split(',');
        if (colors[0] !== '*') {
          shopItems = shopItems.filter(
            (r) =>
              !!r.product.variants.find((v) =>
                colors.includes(v?.color?.shopifyKey)
              )
          );
          shopItems = shopItems.map((shopItem) => {
            const variants = shopItem.product.variants.filter((v) => {
              return colors.includes(v.color?.shopifyKey);
            });
            return {
              ...shopItem,
              defaultProductVariant: variants[0],
              product: { ...shopItem.product, variants },
            };
          });
        }
      }
      shopItems = shopItems.map((s) => ({
        ...s,
        defaultProductVariant:
          s.product.variants[
            Math.ceil((Math.random() * 10000000) % s.product.variants.length)
          ],
      }));
    }

    shopItems = shopItems.filter((s) => !!s.defaultProductVariant);
    return shopItems;
  }, [answers, results]);

  useEffect(() => {
    if (typeof ga === 'undefined') {
      return;
    }
    ga(
      'send',
      'event',
      'Quiz',
      resultTag?.text || 'Results',
      resultTag?.text || 'Results'
    );
  }, []);

  const heroProps = useMemo(() => {
    const props = formatContentfulComponentProps(hero);
    if (props?.button) {
      props.button.onClick = resetQuiz;
    }

    return props;
  }, [hero]);

  return (
    <>
      <BlackFridayHero {...heroProps} />
      <div className='w-full'>
        <section className='my-16'>
          {gridItems?.length > 0 ? (
            <Grid
              key={JSON.stringify(answers)}
              grid={{
                ...grid,
                gridItems,
                targetDiopter: answers?.correction,
              }}
            />
          ) : (
            <Label {...emptyStateLabel} />
          )}
        </section>
      </div>
      {banner ? <Component {...banner} /> : null}
    </>
  );
};

export default QuizResult;
